/**
 * Seeded PRNG utility for reproducible game generation.
 * Uses a mulberry32-style algorithm — fast, good distribution, 32-bit state.
 */

export interface SeededRng {
  /** Returns a float in [0, 1) like Math.random() */
  random(): number;
  /** Returns an integer in [min, max] (inclusive) */
  randomInt(min: number, max: number): number;
  /** Fisher-Yates shuffle (in-place, returns same array) */
  shuffle<T>(array: T[]): T[];
}

/**
 * Create a seeded PRNG instance. The same seed always produces the
 * same sequence of numbers, enabling deterministic level generation.
 */
export function createSeededRng(seed: number): SeededRng {
  let s = seed | 0;

  function random(): number {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  function randomInt(min: number, max: number): number {
    return min + Math.floor(random() * (max - min + 1));
  }

  function shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  return { random, randomInt, shuffle };
}
